import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { useRequest } from 'ahooks';
import { Divider, Input, message, ModalProps, Pagination, Radio, Select } from 'antd';
import Empty from 'components/Empty';
import SafeImage from 'components/SafeImage';
import StickyModal from 'components/StickyModal';
import { useEffect, useState } from 'react';
import { productList } from 'services/api/conversation';

function ItemSelectModal({
  visible,
  storeId,
  item,
  onChange,
  onCancel,
  ...otherProps
}: {
  storeId: number;
  item: Pick<Conversation.Trade.Product, 'externalItemId' | 'itemPic' | 'itemName'>;
  onChange: (
    item: Pick<Conversation.Trade.Product, 'externalItemId' | 'itemPic' | 'itemName'>,
    skuInfo: Conversation.ProductSKUInfo[],
  ) => void;
} & ModalProps) {
  const { t } = useTranslate();

  const [activeItem, setActiveItem] = useState<Conversation.Product>();
  useEffect(() => {
    setActiveItem(undefined);
  }, [item.externalItemId]);

  const [keyword, setKeyword] = useState('');
  const [keywordType, setKeywordType] = useState<0 | 1 | 2 | 3>(0);
  const [_keywordType, set_keywordType] = useState<0 | 1 | 2 | 3>(0);

  const {
    loading,
    data: { itemList, pageInfo } = {},
    runAsync,
  } = useRequest(
    ({ page, pageSize } = { page: 1, pageSize: 10 }) => {
      return productList({
        internal_store_id: storeId,
        keyword,
        keyword_type: _keywordType,
        page_no: page,
        page_size: pageSize,
        needStockRule: true,
        warehouseType: 'REISSUE_WAREHOUSE',
      });
    },
    {
      refreshDeps: [storeId, keyword, _keywordType],
      ready: visible,
      onError: () => message.error(t('common.feedback.fetchFailed')),
    },
  );

  return (
    <StickyModal
      visible={visible}
      title={t('knowledge.修改xxx', {
        x: t('scene.chatWorkspace.taskTab.erpOrderForm.label.product'),
      })}
      width={602}
      onCancel={onCancel}
      onOk={(e) => {
        onCancel?.(e);
        if (activeItem && activeItem.externalItemId !== item.externalItemId) {
          onChange(
            {
              externalItemId: activeItem.externalItemId,
              itemName: activeItem.itemName,
              itemPic: activeItem.imageUrl[0],
            },
            activeItem.skuInfo,
          );
        }
      }}
      {...otherProps}
    >
      <ItemSelectContent>
        <header>
          <Input.Search
            allowClear
            placeholder={t('common.placeholder.input')}
            onSearch={(value) => {
              setKeyword(value);
              set_keywordType(keywordType);
            }}
            addonBefore={
              <Select
                dropdownMatchSelectWidth={false}
                value={keywordType}
                onChange={setKeywordType}
                options={[
                  { value: 0, label: t('domain.product.field.name') },
                  { value: 1, label: t('domain.product.field.id') },
                  { value: 2, label: 'SKU' },
                  { value: 3, label: t('domain.product.field.skuCode') },
                ]}
              />
            }
          />
        </header>
        {itemList?.length ? (
          <>
            <ul>
              {itemList.map((cur) => {
                const {
                  internalItemId,
                  externalItemId,
                  imageUrl: [itemPic],
                  itemName,
                  itemSku,
                } = cur;
                const checked = activeItem
                  ? activeItem.internalItemId === internalItemId
                  : item.externalItemId === externalItemId;
                return (
                  <li onClick={() => setActiveItem(cur)}>
                    <Radio checked={checked} />
                    <SafeImage src={itemPic} width={64} height={64} />
                    <section>
                      <div>{itemName}</div>
                      <p>
                        ID：{externalItemId}
                        <Divider type="vertical" />
                        {t('主商品货号')}：{itemSku || '-'}
                      </p>
                    </section>
                  </li>
                );
              })}
            </ul>
            <footer>
              <Pagination
                size="small"
                showQuickJumper
                showSizeChanger
                current={pageInfo?.pageNo}
                pageSize={pageInfo?.pageSize}
                total={pageInfo?.totalCount}
                onChange={(page, pageSize) => runAsync({ page, pageSize })}
              />
            </footer>
          </>
        ) : loading ? (
          <LoadingOutlined />
        ) : (
          <Empty />
        )}
      </ItemSelectContent>
    </StickyModal>
  );
}

const ItemSelectContent = styled.div`
  margin: -24px;
  > header {
    padding: 10px 16px;
    border-bottom: solid 1px #f0f2f5;
    > span {
      width: 300px;
    }
  }
  > ul {
    margin: 0;
    padding: 0;
    list-style: none;
    > li {
      display: flex;
      align-items: center;
      margin: 16px;
      padding: 10px;
      border: solid 1px #d3d7e0;
      cursor: pointer;
      > div {
        margin-right: 8px;
      }
      > section {
        flex: 1 0 0;
        align-self: stretch;
        display: flex;
        flex-direction: column;
        justify-content: space-around;
        overflow: hidden;
        > div {
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
        > p {
          margin-bottom: 0;
          font-size: 12px;
          color: #84868c;
        }
      }
    }
  }
  > footer {
    margin: 16px;
    > ul {
      text-align: right;
    }
  }
`;

export default ItemSelectModal;
